import {
  Bank,
  DocumentClass,
  DocumentClassSource,
  DocumentSource,
  SourceCheck,
  ValidationResultReport,
} from './apiTypes';
import {
  fetchDocumentBankById,
  fetchGeneralDocumentClasses,
  findChangedSourcesForClass,
} from './functionApi';
import { classesWithLabel } from '../utils/validationLabels';

/** Una fonte in attesa di decisione (`keep` / `switch`), con il contesto per mostrarla. */
export interface PendingSourceDecision {
  /** Nome della banca, oppure 'general' per le classi non legate a una banca. */
  entity: string;
  documentType: string;
  documentClassId: string;
  source: DocumentSource;
  link: DocumentClassSource;
  lastCheck: SourceCheck;
}

export const GENERAL_ENTITY = 'general';

/**
 * Risale dalle classi segnalate `source_changed` alle fonti da risolvere.
 *
 * /validate nomina la classe, non la fonte, e l'elenco dei link non porta ancora
 * l'ultimo outcome del check: servono una o due chiamate in piu' per classe.
 * Le classi ne hanno poche, quindi il costo resta basso.
 */
export const findPendingSourceDecisions = async (
  validation: ValidationResultReport,
  banks: Bank[],
): Promise<PendingSourceDecision[]> => {
  const flagged = classesWithLabel(validation, 'source_changed');
  if (flagged.length === 0) return [];

  // Un fetch di classi per entita', non uno per classe segnalata.
  const entities = Array.from(new Set(flagged.map((f) => f.entity)));
  const classesByEntity = new Map<string, DocumentClass[]>();

  await Promise.all(
    entities.map(async (entity) => {
      try {
        if (entity === GENERAL_ENTITY) {
          classesByEntity.set(entity, await fetchGeneralDocumentClasses());
          return;
        }
        const bank = banks.find((b) => b.name === entity);
        if (!bank) {
          console.error(`Banca "${entity}" non trovata fra quelle caricate: fonti cambiate non risolvibili.`);
          return;
        }
        classesByEntity.set(entity, await fetchDocumentBankById(bank.id));
      } catch (err) {
        console.error(`Classi documento non caricabili per "${entity}":`, err);
      }
    }),
  );

  const decisions = await Promise.all(
    flagged.map(async ({ entity, documentType }) => {
      const documentClass = classesByEntity
        .get(entity)
        ?.find((c) => c.document_type === documentType);
      if (!documentClass) return [];

      try {
        const changed = await findChangedSourcesForClass(documentClass.id);
        return changed.map((c) => ({
          entity,
          documentType,
          documentClassId: documentClass.id,
          ...c,
        }));
      } catch (err) {
        console.error(`Fonti cambiate non leggibili per ${entity}/${documentType}:`, err);
        return [];
      }
    }),
  );

  return decisions.flat();
};
